/**
 * ============================================================================
 *  File        : frontend/src/api/adjustments.ts
 *  Module      : api
 *  Purpose     : メンバー決策 API — 落札 < 希望/最低単価時 三選一
 *                （希望調整 price_adjust / 返品 return / 預り hold）
 *  Author      : EF Engineering
 *  Created     : 2026-06-01
 *  Updated     : 2026-06-01
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端 handler：crates/api-http v13_member_decisions（v1 alias，keyed 到 v13
 *  market_cases 的 case_id）。
 *  - POST /api/v1/cases/:case_id/adjustments → record_decision（Idempotency-Key）
 *  - GET  /api/v1/cases/:case_id/adjustments → list_decisions
 *  - POST /api/v1/adjustments/:id/cancel     → cancel_decision（Idempotency-Key）
 *  RBAC：create/cancel = marketplace.member_decisions.create；GET = ...view。
 *  業務不變式（business-rules §4 / 工程規格書 §6.3）：
 *    希望調整 J − M ∈ [5,20] 円（J = 落札単価, M = メンバー調整後単価）。
 *    後端僅硬性強制下限 J − M ≥ 5（PriceAdjustTooHigh）；上限 20 為業務規格，
 *    前端做完整 [5,20] 校驗，後端為最終權威。
 *  返品 / 預り 只記錄不結算（業主 BLOCKER Q5/Q6 未 sign-off）。
 *  ⚠️ 単価一律字串型 Decimal，前端不得轉 float（CLAUDE.md §2）。
 */

import { API_BASE, mutationHeaders, requestData, type ApiSchemas } from './client'

/** メンバー決策 request body（對齊後端 RecordMemberDecisionDto）。 */
export type RecordMemberDecisionDto = ApiSchemas['RecordMemberDecisionDto']
/** メンバー決策輸出 DTO（對齊後端 MemberDecisionDto）。 */
export type MemberDecisionDto = ApiSchemas['MemberDecisionDto']

/** 決策種別字面值（與後端 decision_type 字串對齊）。 */
export type DecisionType = 'price_adjust' | 'return' | 'hold'

const CASES_PATH = `${API_BASE}/cases`

/** 記錄メンバー決策（自動帶 Idempotency-Key），回傳建立後的決策。 */
export function recordMemberDecision(
  caseId: number | string,
  body: RecordMemberDecisionDto,
): Promise<MemberDecisionDto> {
  return requestData<MemberDecisionDto>(`${CASES_PATH}/${caseId}/adjustments`, {
    method: 'POST',
    body,
    headers: mutationHeaders(),
  })
}

/** 列出案件メンバー決策。 */
export function listMemberDecisions(caseId: number | string): Promise<MemberDecisionDto[]> {
  return requestData<MemberDecisionDto[]>(`${CASES_PATH}/${caseId}/adjustments`, { method: 'GET' })
}

/** 取消メンバー決策（自動帶 Idempotency-Key）。 */
export function cancelMemberDecision(id: number | string): Promise<MemberDecisionDto> {
  return requestData<MemberDecisionDto>(`${API_BASE}/adjustments/${id}/cancel`, {
    method: 'POST',
    body: {},
    headers: mutationHeaders(),
  })
}
